import React, {
  useRef,
  useState,
  useEffect,
  forwardRef,
  useImperativeHandle,
  useMemo,
  useCallback,
} from "react";
import SearchPanel, { ValueChangedEvent } from "@widgets/SearchPanel"; // 조회조건 Panel
import { fnGetDataSet, fnSave } from "@shared/api/restApi"; // BackEnd API
import { getInitFormData } from "@shared/utils/CommonUtils";
import { showDialog } from "@widgets/CommonDialog"; // Message Box
import { global, globalMenu } from "@shared/libs/Global"; // Login User관련 전역 변수( 로그인 정보)
import SnGrid from "@shared/components/GridV2/SnGrid";
import SnTree from "@shared/components/GridV2/SnTree";
import { menuInfo } from "@shared/type/CommonType";
import SnButton from "@shared/components/SnButton";
import SnSearchBtn from "@shared/components/SnSearchBtn";
import SnResizeBar from "@shared/components/SnResizeBar";
import { ColDef } from "ag-grid-community";
import SnFileUploadBox from "@shared/components/SnFileUploadBox";

interface Props {
  Menuprops: menuInfo;
}

const SN_PMS_PJT_REG_103 = forwardRef(({ Menuprops }: Props, ref) => {
  const { userInfo } = global();

  const searchRef = useRef<any>(null);
  const gridRefs = useRef<{ [key: string]: React.RefObject<any> }>({
    tree: React.createRef(),
    grid: React.createRef(),
  });

  const [formData, setFormData] = useState<any>({});
  const [formControlData, setFormControlData] = useState<any[]>([]);
  const [columns, setColums] = useState<any>({
    tree: [],
    grid: [],
    sheet1: [],
  });
  const [dataSources, setDataSources] = useState<any>({
    tree: [],
    grid: [],
    sheet1: [],
  });

  const sheetref1 = useRef<any>(null); // excel download 용
  const [isTreeExpanded, setIsTreeExpanded] = useState<boolean>(true);
  const [selectedDocu, setSelectedDocu] = useState<string>("");

  useEffect(() => {
    const fetchform = async () => {
      try {
        const rtn: any = await getInitFormData();
        setFormData(rtn.formData);
        setFormControlData(rtn.formControlData);
        setColums({
          tree: rtn.grid.grid1,
          grid: rtn.grid.grid2,
          sheet1: rtn.grid.sheet1,
        });
      } catch (error) {}
    };
    fetchform();
  }, []);

  useEffect(() => {
    // PMS 메뉴에서만 품목 도움창 전용 조회 ID를 사용하도록 플래그 주입
    searchRef.current?.applyPopupConditions?.({
      use_pms_item_popup: "Y",
    });
  }, []);

  useImperativeHandle(ref, () => ({
    onSearch,
    onInsert,
    onDelete,
    onSave,
    onExcelDownLoad,
  }));

  const onSearchPanelValueChanged = useCallback((event: ValueChangedEvent) => {
    if (event.colId !== "cd_pjt") return {};

    const oldPjt = String(event.oldValue ?? "").trim();
    const newPjt = String(event.newValue ?? "").trim();
    if (oldPjt === newPjt) return {};

    setDataSources({
      tree: [],
      grid: [],
      sheet1: [],
    });
    setSelectedDocu("");

    return {
      cd_item: "",
      nm_item: "",
      dc_item_spec: "",
      dc_item_unit: "",
      nm_model: "",
      yn_use: "",
      no_rev: "",
    };
  }, []);

  // 조회조건으로 트리/그리드 데이터를 조회하고 화면 데이터를 갱신
  const onSearch = async (overrideForm?: any) => {
    try {
      const form = {
        ...(searchRef.current?.getFormdata?.() ?? {}),
        ...(overrideForm || {}),
      };

      if (!form?.cd_pjt?.trim()) {
        // showDialog("alter", "프로젝트 코드를 선택하시기 바랍니다");
        return false;
      }

      if (!form?.cd_item?.trim()) {
        // showDialog("alter", "품목을 선택하시기 바랍니다");
        return false;
      }

      const modifiedRows = gridRefs.current["grid"].current.getChange();
      if (modifiedRows.modifiedRowcount > 0) {
        const confirm = await showDialog(
          "confirmCancel",
          "수정된 내용이 있습니다. 저장하시겠습니까?"
        );
        if (confirm) {
          const isSaved: any = await onSave();
          if (!isSaved) {
            return;
          }
        } else if (confirm === undefined) {
          return;
        }
      }

      const params = {
        id: "sn_pms_pjt_reg_103_01",
        params: form,
      };

      gridRefs.current["tree"].current.resetUpdate();
      gridRefs.current["grid"].current.resetUpdate();
      // 조회 전 이전 선택/포커스/필터 초기화
      gridRefs.current["tree"].current.clearFocus?.();
      gridRefs.current["grid"].current.clearFocus?.();
      gridRefs.current["grid"].current.resetFilters?.();
      const data: any = await fnGetDataSet(params);
      if (data) {
        // 조회 원본을 먼저 정규화해서 undefined row 유입을 차단
        const rows = Array.isArray(data?.[0])
          ? data[0].filter((row: any) => !!row && typeof row === "object")
          : [];
        const treeData = convertToTreeData(rows);
        setDataSources({
          tree: treeData,
          grid: rows,
          sheet1: rows,
        });
        setSelectedDocu(form.cd_pjt ?? "");
      }
    } catch (error) {}
  };

  // 공통 툴바 인터페이스 유지용(승인등록 화면에서는 추가/삭제 미사용)
  const onInsert = async () => {};
  const onDelete = () => {};

  // 그리드 변경분을 EBOMH/EBOML 저장 payload로 변환하여 저장
  const onSave = async () => {
    try {
      gridRefs.current["grid"].current.stopEditing();

      const requiredValuesCheckH =
        gridRefs.current["grid"].current.requiredValuesCheck();
      if (!requiredValuesCheckH) {
        return false;
      }

      const gridGetChanges = gridRefs.current["grid"].current.getChange();
      if (gridGetChanges.modifiedRowcount == 0) {
        await showDialog("alter", "변경된 내용이 없습니다.");
        return false;
      }

      const form = searchRef.current?.getFormdata?.() ?? {};
      const toNumber = (val: any, fallback = 0) => {
        const num = Number(val);
        return isNaN(num) ? fallback : num;
      };
      const toLine = (row: any) => {
        const cdFirm = row.cd_firm ?? userInfo.cd_firm;
        const cdPjt = row.cd_pjt ?? form.cd_pjt;
        const cdUnit = row.cd_unit ?? form.cd_item;
        const cdItem = row.cd_item ?? form.cd_item;
        const noHst = row.no_hst ?? 0;
        const noRev = row.no_rev ?? "0";
        const stBom = row.st_bom ?? form.st_bom ?? "1";
        return {
          // UUID 컬럼에는 빈 문자열("")을 보내지 않고 null 처리
          no_id_ebomh: row.no_id_ebomh ? String(row.no_id_ebomh) : null,
          no_line: row.no_line ?? 0,
          no_line_p: row.no_line_p ?? 0,
          no_level: row.no_level ?? 0,
          no_seq: row.no_seq ?? 0,
          cd_matl: row.cd_matl ?? "",
          qt_just: row.qt_just ?? 0,
          rt_loss: row.rt_loss ?? 0,
          qt_need: row.qt_need ?? 0,
          dc_rmk: row.dc_rmk ?? "",
          fg_eco: row.fg_eco ?? null,
          cd_cb: row.cd_cb ?? "",
          no_cbs: row.no_cbs ?? "",
          cd_firm: cdFirm,
          cd_pjt: cdPjt,
          cd_unit: cdUnit,
          cd_item: cdItem,
          no_hst: toNumber(noHst, 0),
          no_rev: noRev,
          st_bom: stBom,
          dc_revision: row.dc_revision ?? form.dc_revision ?? "",
          cd_emp: row.cd_emp ?? form.cd_emp ?? userInfo.cd_emp ?? "",
          cd_gp: row.cd_gp ?? form.cd_gp ?? userInfo.cd_gp ?? "",
          cd_dept: row.cd_dept ?? form.cd_dept ?? userInfo.cd_dept ?? "",
          cd_user_reg: row.cd_user_reg ?? userInfo.cd_user ?? "",
          yn_use: row.yn_use ?? "Y",
        };
      };

      const insertRows = [...(gridGetChanges.insert || [])];
      const changedRowsAll = [
        ...insertRows,
        ...(gridGetChanges.update || []),
        ...(gridGetChanges.delete || []),
      ];
      const hasRootInChanges = changedRowsAll.some((row: any) => {
        const isLevelRoot = Number(row?.no_level ?? -1) === 0;
        const isLineRoot =
          Number(row?.no_line ?? -1) === 0 &&
          Number(row?.no_line_p ?? -1) === 0;
        return isLevelRoot || isLineRoot;
      });
      const existingRoot = (dataSources.grid || []).find(
        (row: any) =>
          Number(row?.no_level ?? -1) === 0 ||
          (Number(row?.no_line ?? -1) === 0 &&
            Number(row?.no_line_p ?? -1) === 0)
      );
      const hasExistingRootId = !!existingRoot?.no_id_ebomh;
      const needAutoRootInsert =
        insertRows.length > 0 && !hasRootInChanges && !hasExistingRootId;

      if (needAutoRootInsert) {
        insertRows.unshift({
          cd_firm: form.cd_firm ?? userInfo.cd_firm,
          cd_pjt: form.cd_pjt ?? "",
          cd_unit: form.cd_item ?? "",
          cd_item: form.cd_item ?? "",
          cd_matl: form.cd_item ?? "",
          no_hst: Number(form.no_hst ?? 1),
          no_rev: form.no_rev ?? "0",
          st_bom: form.st_bom ?? "1",
          no_line: 0,
          no_line_p: 0,
          no_level: 0,
          no_seq: 0,
          qt_just: 1,
          rt_loss: 0,
          qt_need: 1,
          dc_rmk: "",
          fg_eco: "I",
          cd_cb: "",
          no_cbs: "",
          dc_revision: form.dc_revision ?? "",
          cd_emp: form.cd_emp ?? userInfo.cd_emp ?? "",
          cd_gp: form.cd_gp ?? userInfo.cd_gp ?? "",
          cd_dept: form.cd_dept ?? userInfo.cd_dept ?? "",
          cd_user_reg: userInfo.cd_user ?? "",
          yn_use: "Y",
          no_id_ebomh: null,
        });
      }

      // update/delete 는 기존 데이터(UUID 보유)만 전송
      const updatableRows = (gridGetChanges.update || []).filter(
        (row: any) => !!row?.no_id_ebomh
      );
      const deletableRows = (gridGetChanges.delete || []).filter(
        (row: any) => !!row?.no_id_ebomh
      );

      const SaveData = {
        pms_pjt_eboml: {
          insert: insertRows.map(toLine),
          update: updatableRows.map(toLine),
          delete: deletableRows.map(toLine),
          modifiedRowcount:
            insertRows.length + updatableRows.length + deletableRows.length,
        },
      };

      const ok = await fnSave("SN_PMS_PJT_REG_103_SAVE_01", SaveData);
      if (!ok) {
        return;
      }

      gridRefs.current["grid"].current.resetUpdate();
      return true;
    } catch (error: any) {
      return false;
    }
  };

  const onExcelDownLoad = async () => {
    try {
      const displayedCols = sheetref1.current?.getAllDisplayedColumns() || [];
      const columnKeys = displayedCols.map((c: any) => c.getColId());

      sheetref1.current?.exportDataAsExcel({
        columnKeys,
        skipPinnedBottom: true,
        fileName: `${globalMenu.getState().activeMenuInfo.nm_menu}.xlsx`,
        sheetName: "Sheet1",
      });
    } catch (error: any) {
      showDialog("error", error.message);
    }
  };

  const autoGroupColumnDef = useMemo<ColDef>(() => {
    return {
      headerName: "품목",
      field: "cd_matl_display",
      flex: 1, // 화면 가득 차게
      minWidth: 300,
      editable: false,
      cellRendererParams: {
        // 승인등록(103)은 단일 선택 화면이므로 체크박스 비활성화
        checkbox: false,
        suppressCount: true, // 하위 아이템 개수 숨기기
        suppressDoubleClickExpand: true, // 더블 클릭으로 확장/축소 비활성화
        suppressHoverHighlight: false,
      },
    };
  }, []);

  const onFocusedRowChanged = (eventData: any) => {
    const gridApi = gridRefs.current["grid"].current;
    if (!gridApi) return;

    const selectedPathRaw = String(eventData?.path ?? "").trim();
    if (!selectedPathRaw) {
      gridApi.resetFilters();
      return;
    }

    // 루트 path가 "A>A" 형태로 내려오는 경우가 있어 prefix 매칭 전 정규화
    const normalizePath = (path: string) => {
      const parts = String(path || "")
        .split(">")
        .map((s) => s.trim())
        .filter(Boolean);
      if (parts.length >= 2 && parts[0] === parts[1]) {
        parts.splice(1, 1);
      }
      return parts.join(">");
    };
    const selectedPath = normalizePath(selectedPathRaw);
    const selectedCode = String(
      eventData?.cd_matl ?? eventData?.cd_item ?? ""
    ).trim();

    // 승인등록(103)은 node 기반보다 path prefix 기반 필터가 안정적이다.
    // 이유: 상태/차수 조건으로 node(node_p) 조합이 달라져도 path 계층은 유지되기 때문.
    const matchedNodes = (dataSources.grid || [])
      .filter((row: any) => {
        const p = normalizePath(String(row?.path ?? ""));
        const rowCode = String(row?.cd_matl ?? row?.cd_item ?? "").trim();
        if (!p) return false;

        // path 포맷이 일관되지 않아(루트전체경로 / 부모>자식 혼재) 2가지 모두 허용:
        // 1) 선택 path prefix
        // 2) 선택 품목코드 기준 self + 하위
        const byPath = p === selectedPath || p.startsWith(`${selectedPath}>`);
        const byCode =
          !!selectedCode &&
          (rowCode === selectedCode ||
            p.startsWith(`${selectedCode}>`) ||
            p.includes(`>${selectedCode}>`) ||
            p.endsWith(`>${selectedCode}`));

        return byPath || byCode;
      })
      .map((row: any) => String(row?.node ?? ""))
      .filter((v: string) => !!v);

    if (matchedNodes.length > 0) {
      gridApi.applyFilters?.({ node: matchedNodes });
    } else {
      gridApi.resetFilters?.();
    }
  };

  const getRowId = useCallback(
    (params: any) => String(params.data.node || params.data.cd_matl),
    []
  );

  const onGrid1BtnsClick = async (id: string) => {
    const api = gridRefs.current["tree"].current;
    if (!api) return;

    if (id === "btnToggleExpand") {
      api.setExpandedAll(!isTreeExpanded);
      setIsTreeExpanded(!isTreeExpanded);
    }
  };

  const onApproveBtnsClick = async (id: "confirm" | "approve" | "release") => {
    // MAS 화면 패턴과 동일하게 트리의 selected node(data) 기준으로 payload를 만든다.
    const treeApi = gridRefs.current["tree"].current;
    const selectedNodes = treeApi?.getSelectedNodes?.() || [];
    const selectedRows = selectedNodes
      .map((node: any) => node?.data)
      .filter((row: any) => !!row);

    if (selectedRows.length === 0) {
      await showDialog("alter", "처리할 품목을 선택해 주세요.");
      return;
    }

    const form = searchRef.current?.getFormdata?.() ?? {};
    const pickHeaderId = (row: any) => {
      const idFromRow = (row?.no_id ?? row?.no_id_ebomh ?? "").toString().trim();
      if (idFromRow) return idFromRow;
      const nodeText = String(row?.node ?? "");
      const head = nodeText.split(":")[0]?.trim();
      return head || "";
    };

    const basePayload = {
      cd_firm: form.cd_firm ?? userInfo.cd_firm,
      cd_pjt: form.cd_pjt ?? "",
      cd_unit: form.cd_item ?? "",
      cd_item: form.cd_item ?? "",
      cd_emp: userInfo.cd_emp ?? "",
      cd_user_amd: userInfo.cd_user ?? "",
      selected_rows: selectedRows
        .filter((row: any) => !!row)
        .map((row: any) => {
          const pickedId = pickHeaderId(row);
          return {
            cd_firm: row?.cd_firm ?? form.cd_firm ?? userInfo.cd_firm,
            cd_pjt: row?.cd_pjt ?? form.cd_pjt ?? "",
            cd_unit: row?.cd_unit ?? form.cd_item ?? "",
            // 트리 선택 타겟 품목은 row.cd_matl(현재 노드 품목코드) 기준
            cd_item: row?.cd_matl ?? row?.cd_item ?? "",
            // no_id는 self UUID 원본, no_id_ebomh는 하위 호환용
            no_id: pickedId || null,
            no_id_ebomh: pickedId || null,
          };
        }),
    };
    if (id === "confirm") {
      const ok = await showDialog("confirmCancel", "변경승인 하시겠습니까?");
      if (!ok) return;
      const payload = {
        ...basePayload,
        cd_emp_change: userInfo.cd_emp ?? "",
      };
      const saved = await fnSave("SN_PMS_PJT_REG_103_CONFIRM_01", payload);
      if (!saved) return;
      await showDialog("info", "변경승인이 완료되었습니다.");
      await onSearch({ st_bom: "2" });
      return;
    }

    if (id === "approve") {
      const ok = await showDialog("confirmCancel", "승인 하시겠습니까?");
      if (!ok) return;
      const payload = {
        ...basePayload,
        cd_emp_approve: userInfo.cd_emp ?? "",
      };
      const saved = await fnSave("SN_PMS_PJT_REG_103_APPROVE_01", payload);
      if (!saved) return;
      await showDialog("info", "승인이 완료되었습니다.");
      await onSearch({ st_bom: "3" });
      return;
    }

    if (id === "release") {
      const ok = await showDialog("confirmCancel", "배포 하시겠습니까?");
      if (!ok) return;
      const payload = {
        ...basePayload,
        cd_emp_release: userInfo.cd_emp ?? "",
      };
      const saved = await fnSave("SN_PMS_PJT_REG_103_RELEASE_01", payload);
      if (!saved) return;
      await showDialog("info", "배포가 완료되었습니다.");
      await onSearch({ st_bom: "4" });
      return;
    }
  };

  const convertToTreeData = (rows: any[]) => {
    if (!rows || rows.length === 0) return [];

    const normalizedRows = rows
      .filter((row: any) => !!row)
      .map((row: any) => ({
        ...row,
        node: row?.node ?? row?.cd_matl ?? "",
        node_p: row?.node_p ?? row?.cd_item ?? "",
        cd_matl_display:
          row?.cd_matl_display ??
          `${row?.cd_matl ?? ""} - ${row?.nm_matl ?? ""}`,
        sort_key:
          row?.sort_key !== undefined && row?.sort_key !== null
            ? Number(row.sort_key)
            : 0,
      }));

    // 트리는 "헤더 역할 품목"만 표시:
    // - 루트(no_level=0)
    // - 다른 행의 cd_item으로 참조되는 cd_matl(= 하위 확장 가능한 품목)
    const headerItemSet = new Set(
      normalizedRows
        .map((row: any) => String(row?.cd_item ?? "").trim())
        .filter((v: string) => !!v)
    );

    const headerRows = normalizedRows.filter((row: any) => {
      const noLevel = Number(row?.no_level ?? 0);
      const cdMatl = String(row?.cd_matl ?? "").trim();
      return noLevel === 0 || (cdMatl !== "" && headerItemSet.has(cdMatl));
    });

    // node 중복 제거
    const seen = new Set<string>();
    return headerRows.filter((row: any) => {
      const nodeKey = String(row?.node ?? "");
      if (!nodeKey || seen.has(nodeKey)) return false;
      seen.add(nodeKey);
      return true;
    });
  };

  const onRowDataReady = () => {
    // 조회 후 이전 선택/포커스 흔적을 제거한다.
    const treeApi = gridRefs.current["tree"].current;
    const gridApi = gridRefs.current["grid"].current;
    if (!treeApi || !gridApi) return;

    treeApi.forEachNode?.((node: any) => node?.setSelected?.(false));
    treeApi.deselectAll?.();
    treeApi.clearFocus?.();
    gridApi.clearFocus?.();
  };
  const grids = useMemo(
    () => ({
      tree: {
        columns: columns.tree,
        dataSource: dataSources.tree,
        events: {
          onFocusedRowChanged,
          onRowDataReady: onRowDataReady,
        },
      },
      grid: {
        columns: columns.grid,
        dataSource: dataSources.grid,
        events: {},
      },
      sheet1: {
        columns: columns.sheet1,
        dataSource: dataSources.sheet1,
      },
    }),
    [columns, dataSources]
  );

  //#region UI
  return (
    <div className="h-full flex flex-col">
      <div className="mb-1">
        <SearchPanel
          ref={searchRef}
          data={formData}
          controls={formControlData}
          onValueChanged={onSearchPanelValueChanged}
        />
      </div>

      <div className="w-full flex flex-1 min-h-0 space-x-1.5 mb-1.5">
        <SnResizeBar direction={"width"} id="panel1" orgSize="25%">
          <SnTree
            ref={gridRefs.current["tree"]}
            columnConfig={grids.tree.columns}
            rowData={grids.tree.dataSource}
            etcProps={{
              // 승인등록 화면은 멀티 선택 기준으로 변경승인/승인/배포 처리
              rowSelection: "multiple",
              title: "품목정보",
            }}
            header={
              <div className="flex items-center gap-2">
                <SnButton
                  text={isTreeExpanded ? "전체닫기" : "전체전개"}
                  onClick={() => onGrid1BtnsClick("btnToggleExpand")}
                />
                <SnSearchBtn
                  text="변경승인"
                  onClick={() => onApproveBtnsClick("confirm")}
                  sx={{
                    ml: 0,
                    minWidth: "74px",
                    height: "30px",
                    borderRadius: "16px",
                    px: "10px",
                    fontSize: "13px",
                    fontWeight: 700,
                    border: "1px solid rgb(0,0,255)",
                    color: "rgb(0,0,255)",
                    backgroundColor: "white",
                    "&:hover": {
                      backgroundColor: "rgb(0,0,255)",
                      color: "white",
                    },
                  }}
                />
                <SnSearchBtn
                  text="승인"
                  onClick={() => onApproveBtnsClick("approve")}
                  sx={{
                    ml: 0,
                    minWidth: "60px",
                    height: "30px",
                    borderRadius: "16px",
                    px: "10px",
                    fontSize: "13px",
                    fontWeight: 700,
                    border: "1px solid rgb(0,0,255)",
                    color: "rgb(0,0,255)",
                    backgroundColor: "white",
                    "&:hover": {
                      backgroundColor: "rgb(0,0,255)",
                      color: "white",
                    },
                  }}
                />
                <SnSearchBtn
                  text="배포"
                  onClick={() => onApproveBtnsClick("release")}
                  sx={{
                    ml: 0,
                    minWidth: "60px",
                    height: "30px",
                    borderRadius: "16px",
                    px: "10px",
                    fontSize: "13px",
                    fontWeight: 700,
                    border: "1px solid rgb(0,0,255)",
                    color: "rgb(0,0,255)",
                    backgroundColor: "white",
                    "&:hover": {
                      backgroundColor: "rgb(0,0,255)",
                      color: "white",
                    },
                  }}
                />
              </div>
            }
            events={grids.tree.events}
            treeProps={{
              treeDataParentIdField: "node_p",
              treeIdKey: "node",
              treeOrderKey: "sort_key",
              groupDefaultExpanded: 5,
              autoGroupColumnDef: autoGroupColumnDef,
              getRowId: getRowId,
            }}
          />
        </SnResizeBar>
        <SnResizeBar direction={"width"} id="panel2" orgSize="74.5%">
          <SnGrid
            ref={gridRefs.current["grid"]}
            columnConfig={grids.grid.columns}
            rowData={grids.grid.dataSource}
            etcProps={{
              rowSelection: "single",
              title: "BOM정보",
            }}
            events={grids.grid.events}
            header={<div />}
          />
        </SnResizeBar>
      </div>

      <SnFileUploadBox selectedDocu={selectedDocu} selectedLineDocu={0} />

      <div style={{ display: "none" }}>
        <SnGrid
          ref={sheetref1}
          columnConfig={grids.sheet1.columns}
          rowData={grids.sheet1.dataSource} // Datasource
          etcProps={{ isEditable: false }}
        />
      </div>
    </div>
  );
});

export default SN_PMS_PJT_REG_103;
